import { Badge, Button, Card, Dialog, EmptyState, Field, Input, Spinner } from '@aurora/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api, ApiError } from '../api/client.js';

interface Member {
  id: string;
  email: string;
  displayName: string;
  isActive: boolean;
  roles: string[];
}

interface Role {
  key: string;
  name: string;
}

/** Einstellungen → Benutzer: Mitglieder einladen, Rollen zuweisen/entziehen. */
export function UsersPage() {
  const queryClient = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ displayName: '', email: '', roleKey: 'member' });
  const [initialPassword, setInitialPassword] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const members = useQuery({
    queryKey: ['users', 'members'],
    queryFn: () => api<{ members: Member[] }>('/api/v1/users'),
  });

  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => api<{ roles: Role[] }>('/api/v1/roles'),
  });

  const refresh = (): void => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const invite = useMutation({
    mutationFn: () =>
      api<{ userId: string; initialPassword?: string }>('/api/v1/users', {
        method: 'POST',
        body: form,
      }),
    onSuccess: (result) => {
      setInviteOpen(false);
      setForm({ displayName: '', email: '', roleKey: 'member' });
      setInitialPassword(result.initialPassword ?? null);
      refresh();
    },
    onError: (mutationError) =>
      setError(
        mutationError instanceof ApiError && mutationError.status === 403
          ? 'Dir fehlt die Berechtigung core.users.manage.'
          : mutationError.message,
      ),
  });

  const changeRole = useMutation({
    mutationFn: (input: { action: 'assign' | 'revoke'; roleKey: string; userId: string }) =>
      api(`/api/v1/roles/${input.roleKey}/${input.action}`, {
        method: 'POST',
        body: { userId: input.userId },
      }),
    onSuccess: refresh,
    onError: (mutationError) => setError(mutationError.message),
  });

  const roleList = roles.data?.roles ?? [];

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 className="page__title">Benutzer</h1>
        <Button onClick={() => setInviteOpen(true)}>Benutzer einladen</Button>
      </div>
      <p className="page__lead">Mitglieder dieses Mandanten und ihre Rollen.</p>
      {error !== null ? <p className="page__error">{error}</p> : null}
      {initialPassword !== null ? (
        <p
          className="page__error"
          style={{ background: 'var(--au-accent-soft)', color: 'var(--au-accent)' }}
        >
          Startpasswort (erscheint nur EINMAL — jetzt weitergeben):{' '}
          <strong>{initialPassword}</strong>
        </p>
      ) : null}

      <Card>
        {members.isLoading ? (
          <Spinner />
        ) : (members.data?.members.length ?? 0) === 0 ? (
          <EmptyState title="Keine Mitglieder" />
        ) : (
          <table className="au-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>E-Mail</th>
                <th>Rollen</th>
                <th aria-label="Rolle zuweisen" />
              </tr>
            </thead>
            <tbody>
              {members.data!.members.map((member) => (
                <tr key={member.id}>
                  <td>
                    <strong>{member.displayName}</strong>
                    {!member.isActive ? <Badge tone="danger">deaktiviert</Badge> : null}
                  </td>
                  <td>{member.email}</td>
                  <td>
                    <span style={{ display: 'flex', gap: 'var(--au-space-1)', flexWrap: 'wrap' }}>
                      {member.roles.map((role) => (
                        <button
                          key={role}
                          type="button"
                          title={`Rolle "${role}" entziehen`}
                          style={{
                            border: 'none',
                            background: 'none',
                            padding: 0,
                            cursor: 'pointer',
                          }}
                          onClick={() =>
                            changeRole.mutate({
                              action: 'revoke',
                              roleKey: role,
                              userId: member.id,
                            })
                          }
                        >
                          <Badge tone="accent">{role} ✕</Badge>
                        </button>
                      ))}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <select
                      className="au-input"
                      style={{ width: 'auto', display: 'inline-block' }}
                      value=""
                      aria-label={`Rolle für ${member.displayName} zuweisen`}
                      onChange={(event) => {
                        if (event.target.value !== '') {
                          changeRole.mutate({
                            action: 'assign',
                            roleKey: event.target.value,
                            userId: member.id,
                          });
                        }
                      }}
                    >
                      <option value="">+ Rolle</option>
                      {roleList
                        .filter((role) => !member.roles.includes(role.key))
                        .map((role) => (
                          <option key={role.key} value={role.key}>
                            {role.name}
                          </option>
                        ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} aria-label="Benutzer einladen">
        <form
          className="au-dialog-form"
          onSubmit={(event) => {
            event.preventDefault();
            invite.mutate();
          }}
        >
          <h2>Benutzer einladen</h2>
          <Field label="Name">
            {(id) => (
              <Input
                id={id}
                required
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              />
            )}
          </Field>
          <Field label="E-Mail">
            {(id) => (
              <Input
                id={id}
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            )}
          </Field>
          <Field label="Rolle">
            {(id) => (
              <select
                id={id}
                className="au-input"
                value={form.roleKey}
                onChange={(e) => setForm({ ...form, roleKey: e.target.value })}
              >
                {(roleList.length > 0 ? roleList : [{ key: 'member', name: 'Mitglied' }]).map(
                  (role) => (
                    <option key={role.key} value={role.key}>
                      {role.name}
                    </option>
                  ),
                )}
              </select>
            )}
          </Field>
          <div className="au-dialog-form__actions">
            <Button variant="ghost" onClick={() => setInviteOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" loading={invite.isPending}>
              Einladen
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
